if (!customElements.get('hero-product-showcase')) {
  customElements.define(
    'hero-product-showcase',
    class HeroProductShowcase extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-hps-track]');
        this.slides = Array.from(this.querySelectorAll('[data-hps-slide]'));
        this.dots = Array.from(this.querySelectorAll('[data-hps-dot]'));
        this.prevBtn = this.querySelector('[data-hps-prev]');
        this.nextBtn = this.querySelector('[data-hps-next]');
        this.index = 0;
        this.autoplay = this.dataset.autoplay === 'true';
        // End arrows hide at first/last; keep loop off unless explicitly enabled.
        this.loop = this.dataset.loop === 'true';
        this.hideArrowsAtEnds = true;
        this.speed = Math.max(2000, Number(this.dataset.autoplaySpeed || 6000));
        this.timer = null;
        this.touchStartX = 0;
        this.touchDeltaX = 0;

        if (this.slides.length <= 1) {
          this.prevBtn?.setAttribute('hidden', '');
          this.nextBtn?.setAttribute('hidden', '');
          this.querySelector('[data-hps-dots]')?.setAttribute('hidden', '');
        }

        this.prevBtn?.addEventListener('click', () => this.goTo(this.index - 1, true));
        this.nextBtn?.addEventListener('click', () => this.goTo(this.index + 1, true));
        this.dots.forEach((dot, i) => {
          dot.addEventListener('click', () => this.goTo(i, true));
        });

        this.querySelectorAll('[data-hps-pause]').forEach((btn) => {
          btn.addEventListener('click', () => this.toggleVideo(btn));
        });

        this.addEventListener('mouseenter', () => this.stopAutoplay());
        this.addEventListener('mouseleave', () => this.startAutoplay());
        this.addEventListener('focusin', () => this.stopAutoplay());
        this.addEventListener('focusout', () => {
          if (!this.contains(document.activeElement)) this.startAutoplay();
        });

        this.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: true });
        this.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: true });
        this.addEventListener('touchend', this.onTouchEnd.bind(this));

        document.addEventListener('visibilitychange', this.onVisibility.bind(this));

        this.goTo(0, false);
        this.startAutoplay();
      }

      disconnectedCallback() {
        this.stopAutoplay();
        document.removeEventListener('visibilitychange', this.onVisibilityBound);
      }

      onVisibility() {
        if (document.hidden) {
          this.stopAutoplay();
          this.pauseAllVideos();
        } else {
          this.playActiveVideo();
          this.startAutoplay();
        }
      }

      onTouchStart(event) {
        this.touchStartX = event.changedTouches[0].screenX;
        this.touchDeltaX = 0;
        this.stopAutoplay();
      }

      onTouchMove(event) {
        this.touchDeltaX = event.changedTouches[0].screenX - this.touchStartX;
      }

      onTouchEnd() {
        if (Math.abs(this.touchDeltaX) > 48) {
          if (this.touchDeltaX < 0) this.goTo(this.index + 1, true);
          else this.goTo(this.index - 1, true);
        }
        this.startAutoplay();
      }

      normalizeIndex(i) {
        const total = this.slides.length;
        if (!total) return 0;
        if (this.loop) return (i + total) % total;
        return Math.max(0, Math.min(total - 1, i));
      }

      updateArrowVisibility() {
        if (!this.prevBtn && !this.nextBtn) return;
        if (this.slides.length <= 1) {
          this.prevBtn?.classList.add('is-hidden');
          this.nextBtn?.classList.add('is-hidden');
          return;
        }

        // Always hide prev on first / next on last so ends are clear.
        const atStart = this.index <= 0;
        const atEnd = this.index >= this.slides.length - 1;

        this.prevBtn?.classList.toggle('is-hidden', atStart);
        this.nextBtn?.classList.toggle('is-hidden', atEnd);

        if (this.prevBtn) {
          this.prevBtn.toggleAttribute('aria-disabled', atStart);
          this.prevBtn.disabled = atStart;
        }
        if (this.nextBtn) {
          this.nextBtn.toggleAttribute('aria-disabled', atEnd);
          this.nextBtn.disabled = atEnd;
        }
      }

      goTo(i, userInitiated = false) {
        if (!this.track || !this.slides.length) return;

        // Without loop, ignore moves past the ends.
        if (!this.loop) {
          if (i < 0 || i > this.slides.length - 1) return;
        }

        this.index = this.normalizeIndex(i);
        this.track.style.transform = `translate3d(${-100 * this.index}%, 0, 0)`;

        this.slides.forEach((slide, idx) => {
          slide.setAttribute('aria-hidden', idx === this.index ? 'false' : 'true');
        });

        this.dots.forEach((dot, idx) => {
          dot.classList.toggle('is-active', idx === this.index);
          dot.setAttribute('aria-current', idx === this.index ? 'true' : 'false');
        });

        this.updateArrowVisibility();
        this.pauseAllVideos();
        this.playActiveVideo();

        if (userInitiated) {
          this.stopAutoplay();
          this.startAutoplay();
        }
      }

      startAutoplay() {
        this.stopAutoplay();
        if (!this.autoplay || this.slides.length <= 1) return;
        this.timer = setInterval(() => {
          if (!this.loop && this.index >= this.slides.length - 1) {
            this.stopAutoplay();
            return;
          }
          this.goTo(this.index + 1);
        }, this.speed);
      }

      stopAutoplay() {
        if (this.timer) {
          clearInterval(this.timer);
          this.timer = null;
        }
      }

      getActiveSlide() {
        return this.slides[this.index];
      }

      getSlideVideo(slide) {
        return slide?.querySelector('.hps__media video');
      }

      pauseAllVideos() {
        this.querySelectorAll('.hps__media video').forEach((video) => {
          video.pause();
          const btn = video.closest('[data-hps-slide]')?.querySelector('[data-hps-pause]');
          btn?.classList.add('is-paused');
        });
      }

      playActiveVideo() {
        const slide = this.getActiveSlide();
        const video = this.getSlideVideo(slide);
        const btn = slide?.querySelector('[data-hps-pause]');
        if (!video) {
          btn?.setAttribute('hidden', '');
          return;
        }
        btn?.removeAttribute('hidden');
        const playPromise = video.play();
        if (playPromise?.then) {
          playPromise
            .then(() => btn?.classList.remove('is-paused'))
            .catch(() => btn?.classList.add('is-paused'));
        }
      }

      toggleVideo(btn) {
        const slide = btn.closest('[data-hps-slide]');
        const video = this.getSlideVideo(slide);
        if (!video) return;

        if (video.paused) {
          video.play();
          btn.classList.remove('is-paused');
        } else {
          video.pause();
          btn.classList.add('is-paused');
        }
      }
    }
  );
}
